/**
 * 优化版拖拽助手类 - 解决卡顿和点击冲突问题
 */
export class DragHelperOptimized {
  constructor(element, options = {}) {
    this.element = element;
    this.options = {
      axis: 'both', // 'x', 'y', 'both'
      bounds: null, // { top, right, bottom, left }
      onStart: null,
      onMove: null,
      onEnd: null,
      threshold: 8, // 增加拖拽阈值，减少误触
      debounceTime: 16, // 节流时间，约60fps
      ...options
    };
    
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.currentX = 0;
    this.currentY = 0;
    this.initialX = 0;
    this.initialY = 0;
    this.hasMoved = false;
    this.startTime = 0;
    this.lastMoveTime = 0;
    this.animationFrame = null;
    this.pendingMove = null;
    
    this.init();
  }
  
  init() {
    // 绑定事件处理函数
    this.boundHandleStart = this.handleStart.bind(this);
    this.boundHandleMove = this.handleMove.bind(this);
    this.boundHandleEnd = this.handleEnd.bind(this);
    
    // 优化：使用passive: false只在必要时阻止默认行为
    this.element.addEventListener('touchstart', this.boundHandleStart, { passive: false });
    document.addEventListener('touchmove', this.boundHandleMove, { passive: false });
    document.addEventListener('touchend', this.boundHandleEnd, { passive: true });
    
    // 鼠标事件支持（用于桌面测试）
    this.element.addEventListener('mousedown', this.boundHandleStart, { passive: false });
    document.addEventListener('mousemove', this.boundHandleMove, { passive: false });
    document.addEventListener('mouseup', this.boundHandleEnd, { passive: true });
  }
  
  handleStart(e) {
    // 记录开始时间
    this.startTime = performance.now();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    this.startX = clientX;
    this.startY = clientY;
    this.initialX = this.currentX;
    this.initialY = this.currentY;
    this.isDragging = true;
    this.hasMoved = false;
    this.lastMoveTime = this.startTime;
    
    // 清理之前的动画帧
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    
    if (this.options.onStart) {
      this.options.onStart(e);
    }
  }
  
  handleMove(e) {
    if (!this.isDragging) return;
    
    const now = performance.now();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const deltaX = clientX - this.startX;
    const deltaY = clientY - this.startY;
    
    // 检查是否超过拖拽阈值
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    if (!this.hasMoved && distance > this.options.threshold) {
      this.hasMoved = true;
      // 只有在确认开始拖拽时才阻止默认行为
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (this.hasMoved) {
      // 节流处理：使用requestAnimationFrame优化性能
      this.pendingMove = {
        e,
        deltaX,
        deltaY,
        clientX,
        clientY,
        timestamp: now
      };
      
      if (!this.animationFrame) {
        this.animationFrame = requestAnimationFrame(() => {
          this.processPendingMove();
        });
      }
    }
  }
  
  processPendingMove() {
    if (!this.pendingMove || !this.isDragging) {
      this.animationFrame = null;
      return;
    }
    
    const { e, deltaX, deltaY } = this.pendingMove;
    
    // 阻止默认行为和事件冒泡
    e.preventDefault();
    e.stopPropagation();
    
    if (this.options.axis === 'x' || this.options.axis === 'both') {
      this.currentX = this.initialX + deltaX;
    }
    if (this.options.axis === 'y' || this.options.axis === 'both') {
      this.currentY = this.initialY + deltaY;
    }
    
    // 应用边界限制
    if (this.options.bounds) {
      this.applyBounds();
    }
    
    if (this.options.onMove) {
      this.options.onMove({
        x: this.currentX,
        y: this.currentY,
        deltaX,
        deltaY,
        hasMoved: this.hasMoved
      });
    }
    
    this.pendingMove = null;
    this.animationFrame = null;
  }
  
  handleEnd(e) {
    if (!this.isDragging) return;
    
    const endTime = performance.now();
    const duration = endTime - (this.startTime || 0);
    
    // 清理动画帧
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    
    // 处理最后一次移动
    if (this.pendingMove) {
      this.processPendingMove();
    }
    
    this.isDragging = false;
    
    // 优化点击冲突判断逻辑
    const shouldPreventClick = this.shouldPreventClick(duration);
    
    if (shouldPreventClick && e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (this.options.onEnd) {
      this.options.onEnd({
        x: this.currentX,
        y: this.currentY,
        hasMoved: this.hasMoved,
        duration: duration,
        shouldPreventClick
      });
    }
    
    // 清理状态
    this.pendingMove = null;
  }
  
  shouldPreventClick(duration) {
    // 如果有明显移动，阻止点击
    if (this.hasMoved) return true;
    
    // 如果持续时间过长（可能是长按），阻止点击
    if (duration > 500) return true;
    
    // 如果持续时间过短（可能是误触），不阻止点击
    if (duration < 100) return false;
    
    return false;
  }
  
  applyBounds() {
    const bounds = this.options.bounds;
    if (bounds.left !== undefined) {
      this.currentX = Math.max(bounds.left, this.currentX);
    }
    if (bounds.right !== undefined) {
      this.currentX = Math.min(bounds.right, this.currentX);
    }
    if (bounds.top !== undefined) {
      this.currentY = Math.max(bounds.top, this.currentY);
    }
    if (bounds.bottom !== undefined) {
      this.currentY = Math.min(bounds.bottom, this.currentY);
    }
  }
  
  setPosition(x, y) {
    this.currentX = x;
    this.currentY = y;
  }
  
  destroy() {
    // 清理动画帧
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    
    if (this.element && this.boundHandleStart) {
      this.element.removeEventListener('touchstart', this.boundHandleStart);
      this.element.removeEventListener('mousedown', this.boundHandleStart);
    }
    
    if (this.boundHandleMove) {
      document.removeEventListener('touchmove', this.boundHandleMove);
      document.removeEventListener('touchend', this.boundHandleEnd);
      document.removeEventListener('mousemove', this.boundHandleMove);
      document.removeEventListener('mouseup', this.boundHandleEnd);
    }
    
    // 清理引用
    this.boundHandleStart = null;
    this.boundHandleMove = null;
    this.boundHandleEnd = null;
    this.element = null;
    this.pendingMove = null;
  }
}